import {
  getCurrentScope,
  onScopeDispose,
  getCurrentInstance,
  onMounted,
  nextTick
} from 'vue-demi';
import type { Fn } from '~/types/global';

/**
 * Attempts to register a disposal function that is called when the current effect scope is disposed.
 * If the current execution context is not within an effect scope, the function does nothing.
 *
 * This is useful for cleaning up or performing final actions when a Vue Composition API's effect scope is destroyed.
 *
 * @param {Fn} fn - A function to be called upon the effect scope's disposal.
 * @returns {boolean} - Returns `true` if the disposal function was successfully registered, `false` otherwise.
 */
export function tryOnScopeDispose(fn: Fn): boolean {
  if (getCurrentScope()) {
    onScopeDispose(fn);
    return true;
  }
  return false;
}

/**
 * Executes a function immediately if called outside of a Vue component's lifecycle, or defers its execution
 * until the component is mounted if called within a component lifecycle. This allows for safe usage of
 * lifecycle-dependent actions in setup functions without worrying about the current lifecycle stage.
 *
 * @param {Fn} fn - The function to be executed.
 * @param {boolean} [sync=true] - If `true`, executes `fn` immediately when outside a component lifecycle.
 *                                If `false`, defers execution to the next tick.
 */
export function tryOnMounted(fn: Fn, sync = true) {
  const instance = getCurrentInstance();
  if (instance) onMounted(fn);
  else if (sync) fn();
  else nextTick(fn);
}
